import { BehaviorSubject, Observable } from 'rxjs';
import { SensorOptionTypeEnum } from './sensor-option-type-model';

export interface IRawPaginatedData<T = any> {
  currentPage: number;
  pageSize: number;
  totalRecords?: number;
  records?: T[];
}
export interface IPaginatorModel<T = any> {
  page: number;
  pageSize: number;
  totalCount?: number;
  totalRecords?: number;
  recordsPerUser?: number;
  data?: T;
}

/**
 * Represents an abstract class for a paginated data source, providing a framework
 * for fetching and managing paginated data. The generic type `T` allows for flexibility in
 * the type of data being paginated.
 *
 * @template T The type of elements contained in the data source.
 */
export abstract class AbstractPaginatorDataSource<T = any> {
  /**
   * An observable stream of data items of type `T`. Subclasses must provide an
   * implementation for this observable that emits the current page of items
   * whenever the data changes.
   */
  abstract data$: Observable<T>;

  /**
   * A behavior subject that holds the current state of the pagination, including
   * information such as the current page, total number of items, and page size.
   * This allows for reactive updates to pagination controls and information display.
   */
  abstract pageInfo: BehaviorSubject<IPaginatorModel>;

  /**
   * An abstract method that must be implemented by subclasses to fetch a specific
   * page of data based on the given pagination information. This method should return
   * an observable that emits the fetched page of data, encapsulated in an `IPaginatorModel`.
   *
   * @param pageInfo An `IPaginatorModel` instance containing the pagination information
   *                 needed to fetch the desired page of data. Includes
   *                 the current page number, page size, and potentially other filters.
   * @returns An `Observable` that emits an `IPaginatorModel` containing the fetched
   *          page of data and updated pagination information.
   */
  abstract fetchPageData(
    pageInfo: IPaginatorModel<T>
  ): Observable<IPaginatorModel>;
}

export abstract class AbstractPanelManagementDataSource<T = any> {
  abstract data$: Observable<T>;
  abstract pageInfo: BehaviorSubject<IPaginatorModel>;
  abstract formFiltersValue?: any;
  abstract init(): void;
  abstract fetchPageData(
    pageInfo: IPaginatorModel<T>
  ): Observable<IPaginatorModel>;
  abstract submitCreateForm:  (formValue: any) => Observable<any>;
  abstract submitEditForm:    (formValue: any) => Observable<any>;
  abstract searchFromFilters: (formFiltersValue: any) => void;
  abstract getSelectOptions:  (optionType: string, value?: number) => Observable<any>;
  abstract getEnableInput?:   (optionType: string, value?: number) => Observable<{enableInput:boolean,patchValue:string}>;
  abstract getRadioOptions?:  (optionType: string) => Observable<any>;
  abstract buildExcelPdfUrl:  (formValue: any) => string;
  abstract getIndividualData?:(rowData: any) => Observable<any>;
  abstract getValueById?:     (id: string, params?: { [key: string]: any }) => Observable<any>;
  abstract getStateCounter?:  () => Observable<any[]>;
  abstract clearFilter?:      () => void;
}

export abstract class IAbstractFormDataSource<T> {
  abstract getSelectOptions : (optionType: string, value?: number) => Observable<ISelectOption[]>;
  abstract getIndividualData: (rowData  : {[key: string]: unknown}) => Observable<T>;
  abstract submitEditForm   : (formValue: {[key: string]: unknown}) => Observable<unknown>;
  abstract submitCreateForm : (formValue: {[key: string]: unknown}) => Observable<unknown>;
}

export interface ISelectOption {
  value: string,
  name: string,
}

export abstract class filterStateDataSource<T = any> {
  abstract filterObservable: Observable<T>;
  abstract saveStateFilter: (formFilter: any) => void;
  abstract filterValue: () => T;
}
